import type { AggregatedQto } from "../../integrations/qto/types";
import { applyGridStyle, edge } from "../styles/worksheet";
import { qtoReviewUomsMatch, ediphiProductIdFromClassificationEdiphiId } from "../../integrations/qto/utils";
import { WORKSHEET_BORDER_COLOR, WORKSHEET_HEADER_FILL, WORKSHEET_SPACER_FILL } from "../styles/colors";
import { getRootAddinUrl } from "../../utils/browser";
import { SHEET_NAME } from "./reviewSheetLayout";
import { canonicalProductMapKey, fetchProductsByIdsForQtoReview } from "../../integrations/qto/reviewClient";
import {
  LINKED_NAME_BROKEN_PRODUCT_ID,
  LINKED_NAME_NO_UPC_LINK,
  QTO_REVIEW_TYPE_WARN,
  findLinkedLineItem,
  qtoReviewRowKind,
  reviewOrderCellInput,
  type EstimateLineItemForReview,
  type ReviewLinkValue,
} from "../../integrations/qto/reviewRow";

const FONT_FAMILY = "Aptos";

const FONT_FAMILY_ACTION = "Aptos Black";

/** Action column conditional formatting (Update / Add). */

const CF_UPDATE_FILL = "#E1F5FF";

const CF_UPDATE_FONT = "#014B6E";

const CF_ADD_FILL = "#C1F0C8";

const CF_ADD_FONT = "#0D3512";

const WARN_FILL = "#FEF9E9";

const WARN_FONT = "#624C0F";

/**
 * Value in the QTO Review Type (link) column when the row is flagged.
 * Single source for sheet cells, conditional-format formulas, and parsing comparisons.
 */

const WARN_COMMENT_BROKEN_UPC = "Broken UPC Link";

const WARN_COMMENT_NO_UPC_LINK = "No UPC Link";

const WARN_COMMENT_MISMATCH_UOM = "Mismatched UoM";

/** Linked item name (column H) when the ediphi product id from the classification is missing in the catalog. */

async function syncWithScreenUpdatingSuspended(context: Excel.RequestContext): Promise<void> {
  context.application.suspendScreenUpdatingUntilNextSync();
  await context.sync();
}

/** Threaded cell comments on Type (G) for each WARN row (avoids ExcelApi 1.18 worksheet `notes`). */

function applyWarnCommentsToTypeColumn(
  sheet: Excel.Worksheet,
  startRow: number,
  warnCommentByRow: (string | null)[]
): void {
  for (let idx = 0; idx < warnCommentByRow.length; idx++) {
    const text = warnCommentByRow[idx];
    if (!text) continue;
    try {
      sheet.comments.add(sheet.getRange(`G${startRow + idx}`), text);
    } catch (e) {
      console.warn(`QTO Review: could not add comment on G${startRow + idx}`, e);
    }
  }
}

const applyThinBorders = (range: Excel.Range, sides: edge[]) => {
  applyGridStyle({ range, borderColor: WORKSHEET_BORDER_COLOR, weight: "Thin", edges: sides });
};

const styleRange = (
  range: Excel.Range,
  opts: { size?: number; bold?: boolean; fill?: string; hAlign?: Excel.HorizontalAlignment }
) => {
  range.format.font.name = FONT_FAMILY;
  range.format.font.color = "#000000";
  if (opts.size !== undefined) range.format.font.size = opts.size;
  if (opts.bold !== undefined) range.format.font.bold = opts.bold;
  if (opts.fill) range.format.fill.color = opts.fill;
  if (opts.hAlign) range.format.horizontalAlignment = opts.hAlign;
};

export async function reviewQTO(
  aggregated: AggregatedQto[],
  lineItems: EstimateLineItemForReview[] = []
): Promise<void> {
  const aggregatedForReview = aggregated.filter((item) => qtoReviewRowKind(item, lineItems) !== null);
  const ediphiProductIds = aggregatedForReview
    .map((item) => {
      if (findLinkedLineItem(item, lineItems)) return null;
      return ediphiProductIdFromClassificationEdiphiId(item.ediphiProductId);
    })
    .filter((id): id is string => id != null && String(id).trim() !== "");
  const productById = await fetchProductsByIdsForQtoReview(ediphiProductIds);

  await Excel.run(async (context) => {
    let sheet = context.workbook.worksheets.getItemOrNullObject(SHEET_NAME);
    let priorSheet: Excel.Worksheet | null = null;
    await syncWithScreenUpdatingSuspended(context);

    if (!sheet.isNullObject) {
      priorSheet = sheet;
      // Build on a new temp worksheet first, then swap it in.
      const tempSheetName = `${SHEET_NAME} (new)`;
      const staleTempSheet = context.workbook.worksheets.getItemOrNullObject(tempSheetName);
      await syncWithScreenUpdatingSuspended(context);
      if (!staleTempSheet.isNullObject) {
        staleTempSheet.delete();
      }
      sheet = context.workbook.worksheets.add(tempSheetName);
    } else {
      sheet = context.workbook.worksheets.add(SHEET_NAME);
    }

    sheet.visibility = Excel.SheetVisibility.visible;

    // A=gutter, B=Action, C=Order, D–F Takeoff, G=Type, H–J Linked name/qty/uom,
    // K–M hidden ids, N=gutter
    const columnWidths = [20, 60, 70, 250, 90, 50, 70, 250, 90, 50, 110, 8, 8, 300];
    columnWidths.forEach((width, i) => {
      sheet.getRange(`${String.fromCharCode(65 + i)}:${String.fromCharCode(65 + i)}`).format.columnWidth = width;
    });

    sheet.getRange("1:1").format.rowHeight = 40;
    sheet.getRange("2:2").format.rowHeight = 20;
    sheet.getRange("3:4").format.rowHeight = 25;
    sheet.getRange("1:4").format.verticalAlignment = Excel.VerticalAlignment.center;

    // White fill for title row
    styleRange(sheet.getRange("A1:N1"), { size: 12, bold: false, fill: "#FFFFFF" });

    // Spacer row 2
    styleRange(sheet.getRange("A2:N2"), { size: 12, bold: false, fill: WORKSHEET_SPACER_FILL });

    // A3:A4 gutter with right border
    styleRange(sheet.getRange("A3:A4"), { size: 12, bold: false, fill: WORKSHEET_SPACER_FILL });
    applyThinBorders(sheet.getRange("A3:A4"), ["EdgeRight"]);

    // N3:N4 gutter with left border
    styleRange(sheet.getRange("N3:N4"), { size: 12, bold: false, fill: WORKSHEET_SPACER_FILL });
    applyThinBorders(sheet.getRange("N3:N4"), ["EdgeLeft"]);

    // Title cells — B1 shows the addin icon, C1 shows "QTO Review"
    const baseUrl = getRootAddinUrl();
    sheet.getRange("B1").values = [[`=IFERROR(IMAGE("${baseUrl}/assets/icon-64.png",,3,20),"")`]];
    styleRange(sheet.getRange("B1"), { fill: "#FFFFFF", hAlign: Excel.HorizontalAlignment.center });

    const c1 = sheet.getRange("C1");
    c1.values = [["QTO Review"]];
    styleRange(c1, { size: 22, bold: true, fill: "#FFFFFF" });

    // Header area base fill & font (K–M = hidden ids)
    styleRange(sheet.getRange("B3:M4"), { size: 12, bold: true, fill: WORKSHEET_HEADER_FILL });

    // Row 3 group headers with borders
    applyThinBorders(sheet.getRange("B3"), ["EdgeLeft", "EdgeRight"]);
    applyThinBorders(sheet.getRange("C3"), ["EdgeLeft", "EdgeRight"]);

    sheet.getRange("D3").values = [["Takeoff"]];
    applyThinBorders(sheet.getRange("D3"), ["EdgeLeft"]);
    applyThinBorders(sheet.getRange("F3"), ["EdgeRight"]);

    sheet.getRange("G3").values = [["Linked Item"]];
    applyThinBorders(sheet.getRange("G3"), ["EdgeLeft"]);
    applyThinBorders(sheet.getRange("J3"), ["EdgeRight"]);

    applyThinBorders(sheet.getRange("K3"), ["EdgeLeft", "EdgeRight"]);
    applyThinBorders(sheet.getRange("L3"), ["EdgeLeft", "EdgeRight"]);
    applyThinBorders(sheet.getRange("M3"), ["EdgeLeft", "EdgeRight"]);

    // Row 4 column headers — all get bottom/left/right borders
    const row4Cells: { addr: string; value: string; align?: Excel.HorizontalAlignment }[] = [
      { addr: "B4", value: "Action", align: Excel.HorizontalAlignment.center },
      { addr: "C4", value: "Order", align: Excel.HorizontalAlignment.center },
      { addr: "D4", value: "Name" },
      { addr: "E4", value: "Quantity", align: Excel.HorizontalAlignment.right },
      { addr: "F4", value: "UoM" },
      { addr: "G4", value: "Type", align: Excel.HorizontalAlignment.center },
      { addr: "H4", value: "Name" },
      { addr: "I4", value: "Quantity", align: Excel.HorizontalAlignment.right },
      { addr: "J4", value: "UoM", align: Excel.HorizontalAlignment.left },
      { addr: "K4", value: "Line Item ID", align: Excel.HorizontalAlignment.center },
      { addr: "L4", value: "Classification ID", align: Excel.HorizontalAlignment.center },
      { addr: "M4", value: "Product ID", align: Excel.HorizontalAlignment.center },
    ];
    for (const h of row4Cells) {
      const r = sheet.getRange(h.addr);
      r.values = [[h.value]];
      applyThinBorders(r, ["EdgeBottom", "EdgeLeft", "EdgeRight"]);
      if (h.align) r.format.horizontalAlignment = h.align;
    }

    // Data rows
    if (aggregatedForReview.length > 0) {
      const startRow = 5;
      const lastRow = startRow + aggregatedForReview.length - 1;
      const firstFooterRow = lastRow + 1;
      /** 300px-tall gray spacer rows below the table (original + four more). */
      const FOOTER_SPACER_COUNT = 5;
      const lastFooterRow = firstFooterRow + FOOTER_SPACER_COUNT - 1;

      // B=Action, C=Order, D–F takeoff, G=Type, H–J linked item, K–M hidden ids.
      const linkedFlags: boolean[] = [];
      const dataValues: (string | number)[][] = [];
      const warnCommentByRow: (string | null)[] = [];

      for (const item of aggregatedForReview) {
        const linked = findLinkedLineItem(item, lineItems);
        linkedFlags.push(!!linked);
        const ediphiProductRef = linked ? null : ediphiProductIdFromClassificationEdiphiId(item.ediphiProductId);
        const productLookupKey = ediphiProductRef != null ? canonicalProductMapKey(ediphiProductRef) : "";
        const productResolved = productLookupKey !== "" ? productById.get(productLookupKey) : undefined;

        let typeCell: ReviewLinkValue = "";
        let productIdCell = "";
        let linkedName = "";
        let linkedQty: string | number = "";
        let linkedUom = "";
        let warnComment: string | null = null;

        if (linked) {
          typeCell = "Line Item";
          linkedName = linked.name;
          linkedQty = linked.quantity;
          linkedUom = linked.uom;
          productIdCell = "";
        } else if (ediphiProductRef != null) {
          if (!productResolved) {
            typeCell = QTO_REVIEW_TYPE_WARN;
            linkedName = LINKED_NAME_BROKEN_PRODUCT_ID;
            warnComment = WARN_COMMENT_BROKEN_UPC;
          } else if (!qtoReviewUomsMatch(item.uom, productResolved.uom)) {
            typeCell = QTO_REVIEW_TYPE_WARN;
            productIdCell = ediphiProductRef;
            warnComment = WARN_COMMENT_MISMATCH_UOM;
            linkedName = productResolved.name ?? "";
            linkedUom = productResolved.uom ?? "";
            console.warn(
              `QTO Review: product UOM does not match takeoff for product ${ediphiProductRef} ("${productResolved.uom}" vs classification "${item.uom}")`
            );
          } else {
            // Displayed label for linked product rows in the Type column.
            typeCell = "UPC Item";
            productIdCell = ediphiProductRef;
            linkedName = productResolved.name ?? "";
            linkedUom = productResolved.uom ?? "";
          }
        } else {
          typeCell = QTO_REVIEW_TYPE_WARN;
          linkedName = LINKED_NAME_NO_UPC_LINK;
          warnComment = WARN_COMMENT_NO_UPC_LINK;
        }

        const lineItemIdCell = linked ? linked.id : "";
        dataValues.push([
          linked ? "Update" : "Add",
          reviewOrderCellInput(item),
          item.qtoName,
          Math.round(item.quantity),
          item.uom,
          typeCell,
          linkedName,
          linkedQty,
          linkedUom,
          lineItemIdCell,
          String(item.qtoId ?? ""),
          productIdCell,
        ]);
        warnCommentByRow.push(warnComment);
      }

      sheet.getRange(`${startRow}:${lastRow}`).format.rowHeight = 22;
      sheet.getRange(`${startRow}:${lastRow}`).format.verticalAlignment = Excel.VerticalAlignment.center;

      const dataRange = sheet.getRange(`B${startRow}:M${lastRow}`);
      sheet.getRange(`C${startRow}:C${lastRow}`).format.horizontalAlignment = Excel.HorizontalAlignment.right;

      dataRange.values = dataValues;
      styleRange(dataRange, { size: 12, bold: false, fill: "#FFFFFF" });

      // Borders on every data cell (all inner + outer edges)
      applyThinBorders(dataRange, [
        "EdgeTop",
        "EdgeBottom",
        "EdgeLeft",
        "EdgeRight",
        "InsideVertical",
        "InsideHorizontal",
      ]);

      const takeoffQtyFormat = aggregatedForReview.map(() => ["#,##0"]);
      const lineItemQtyFormat = aggregatedForReview.map(() => ["#,##0.00"]);
      sheet.getRange(`E${startRow}:E${lastRow}`).numberFormat = takeoffQtyFormat;
      sheet.getRange(`E${startRow}:E${lastRow}`).format.horizontalAlignment = Excel.HorizontalAlignment.right;
      sheet.getRange(`I${startRow}:I${lastRow}`).numberFormat = lineItemQtyFormat;
      sheet.getRange(`I${startRow}:I${lastRow}`).format.horizontalAlignment = Excel.HorizontalAlignment.right;
      sheet.getRange(`J${startRow}:J${lastRow}`).format.horizontalAlignment = Excel.HorizontalAlignment.left;

      const actionRange = sheet.getRange(`B${startRow}:B${lastRow}`);
      actionRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      actionRange.format.font.name = FONT_FAMILY_ACTION;

      for (let i = 0; i < aggregatedForReview.length; i++) {
        const actionCell = sheet.getRange(`B${startRow + i}`);
        const isLinked = linkedFlags[i];
        actionCell.dataValidation.rule = {
          list: { inCellDropDown: true, source: isLinked ? "Update" : "Add" },
        };
        actionCell.dataValidation.errorAlert = {
          showAlert: true,
          title: "Invalid action",
          message: isLinked ? "Please select Update or leave empty." : "Please select Add or leave empty.",
          style: Excel.DataValidationAlertStyle.stop,
        };
      }

      // Action column (B): colors keyed off Action value.
      const actionOnlyCf = sheet.getRange(`B${startRow}:B${lastRow}`);
      const cfActUpdate = actionOnlyCf.conditionalFormats.add(Excel.ConditionalFormatType.custom);
      cfActUpdate.custom.rule.formula = `=AND($B${startRow}="Update",$G${startRow}<>"${QTO_REVIEW_TYPE_WARN}")`;
      cfActUpdate.custom.format.fill.color = CF_UPDATE_FILL;
      cfActUpdate.custom.format.font.color = CF_UPDATE_FONT;
      cfActUpdate.custom.format.font.bold = true;

      const cfActAdd = actionOnlyCf.conditionalFormats.add(Excel.ConditionalFormatType.custom);
      cfActAdd.custom.rule.formula = `=AND($B${startRow}="Add",$G${startRow}<>"${QTO_REVIEW_TYPE_WARN}")`;
      cfActAdd.custom.format.fill.color = CF_ADD_FILL;
      cfActAdd.custom.format.font.color = CF_ADD_FONT;
      cfActAdd.custom.format.font.bold = true;

      const cfActWarn = actionOnlyCf.conditionalFormats.add(Excel.ConditionalFormatType.custom);
      cfActWarn.custom.rule.formula = `=$G${startRow}="${QTO_REVIEW_TYPE_WARN}"`;
      cfActWarn.custom.format.fill.color = WARN_FILL;
      cfActWarn.custom.format.font.color = WARN_FONT;
      cfActWarn.custom.format.font.bold = true;

      const typeRange = sheet.getRange(`G${startRow}:G${lastRow}`);
      typeRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;

      // Linked block G–J: Action styling unless Type is WARN (WARN rule added last = top priority + stopIfTrue).
      const linkedBlockRange = sheet.getRange(`G${startRow}:J${lastRow}`);
      const cfLiUpdate = linkedBlockRange.conditionalFormats.add(Excel.ConditionalFormatType.custom);
      cfLiUpdate.custom.rule.formula = `=AND($B${startRow}="Update",$G${startRow}<>"${QTO_REVIEW_TYPE_WARN}")`;
      cfLiUpdate.custom.format.fill.color = CF_UPDATE_FILL;
      cfLiUpdate.custom.format.font.color = CF_UPDATE_FONT;
      cfLiUpdate.custom.format.font.bold = true;

      const cfLiAdd = linkedBlockRange.conditionalFormats.add(Excel.ConditionalFormatType.custom);
      cfLiAdd.custom.rule.formula = `=AND($B${startRow}="Add",$G${startRow}<>"${QTO_REVIEW_TYPE_WARN}")`;
      cfLiAdd.custom.format.fill.color = CF_ADD_FILL;
      cfLiAdd.custom.format.font.color = CF_ADD_FONT;
      cfLiAdd.custom.format.font.bold = true;

      const cfWarn = linkedBlockRange.conditionalFormats.add(Excel.ConditionalFormatType.custom);
      cfWarn.custom.rule.formula = `=$G${startRow}="${QTO_REVIEW_TYPE_WARN}"`;
      cfWarn.custom.format.fill.color = WARN_FILL;
      cfWarn.custom.format.font.color = WARN_FONT;
      cfWarn.custom.format.font.bold = true;
      cfWarn.stopIfTrue = true;

      applyWarnCommentsToTypeColumn(sheet, startRow, warnCommentByRow);

      // Spacer fills: column A gutter, column N gutter, and footer row
      styleRange(sheet.getRange(`A${startRow}:A${lastRow}`), { size: 12, bold: false, fill: WORKSHEET_SPACER_FILL });
      applyThinBorders(sheet.getRange(`A${startRow}:A${lastRow}`), ["EdgeRight"]);

      styleRange(sheet.getRange(`N${startRow}:N${lastRow}`), { size: 12, bold: false, fill: WORKSHEET_SPACER_FILL });
      applyThinBorders(sheet.getRange(`N${startRow}:N${lastRow}`), ["EdgeLeft"]);

      sheet.getRange(`${firstFooterRow}:${lastFooterRow}`).format.rowHeight = 300;
      styleRange(sheet.getRange(`A${firstFooterRow}:N${lastFooterRow}`), {
        size: 12,
        bold: false,
        fill: WORKSHEET_SPACER_FILL,
      });
      applyThinBorders(sheet.getRange(`B${firstFooterRow}:M${firstFooterRow}`), ["EdgeTop"]);

      // Hide all rows after the footer spacers
      sheet.getRange(`${lastFooterRow + 1}:1048576`).rowHidden = true;

      sheet.autoFilter.apply(sheet.getRange(`B4:J${lastRow}`));
    }

    // Column N (right gutter); hide unused columns to the right (not K–M yet)
    sheet.getRange("N:N").format.columnWidth = 300;
    sheet.getRange("O:XFD").columnHidden = true;

    sheet.freezePanes.freezeRows(4);

    sheet.showGridlines = false;
    sheet.showHeadings = true;

    sheet.getRange("K:M").columnHidden = true;

    if (priorSheet) {
      // Activate new sheet before deleting old to avoid deleting the active worksheet.
      sheet.activate();
      priorSheet.delete();
      sheet.name = SHEET_NAME;
    }

    sheet.activate();
    await syncWithScreenUpdatingSuspended(context);
  });
}

// ---------------------------------------------------------------------------
// Read, CSV build, and import helpers
// ---------------------------------------------------------------------------
